import {GoogleMap, useLoadScript} from '@react-google-maps/api';
import PropTypes from 'prop-types';
import {
  allPass,
  complement,
  filter,
  isNil,
  pipe,
  prop,
  propIs,
  uniqBy,
  isEmpty,
} from 'ramda';
import React, {useEffect} from 'react';
import {withRouter} from 'react-router';
import styled from 'styled-components';
import {GET_SYSTEM_LOCATIONS} from '.';
import Loading from '../../molecules/Loading';
import {useQueryString} from '../../utils/hooks';
import MapContent from './MapContent';
import {useQuery} from '@apollo/react-hooks';

const Wrapper = styled.div`
  height: 100vh;
  flex: 1;
  & > div {
    height: 100%;
  }
`;

const {REACT_APP_GM_KEY} = process.env;

const hasLocation = pipe(
  prop('location'),
  allPass([
    complement(isNil),
    propIs(Number, 'latitude'),
    propIs(Number, 'longitude'),
  ]),
);

const filterSystems = pipe(filter(hasLocation), uniqBy(prop('id')));
const CENTER = {lat: 28, lng: 21};

const GlobalMapPage = ({onError, history}) => {
  const {isLoaded, loadError} = useLoadScript({
    googleMapsApiKey: REACT_APP_GM_KEY,
  });
  const [center, setCenter] = React.useState(CENTER);
  const [zoom, setZoom] = React.useState(3);

  const {error, data} = useQuery(GET_SYSTEM_LOCATIONS, {
    pollInterval: 60000,
    fetchPolicy: 'network-only',
  });

  const params = useQueryString();
  const systemId = params.get('system');
  const map = React.useRef();
  const selectedSystem = data ? data.systems.find(s => s.id === systemId) : {};

  useEffect(() => {
    if (!selectedSystem || isEmpty(selectedSystem)) {
      return;
    }
    if (zoom < 4) {
      setCenter({
        lat: selectedSystem.location.latitude,
        lng: selectedSystem.location.longitude,
      });
      setZoom(4);
    }
    // zoom had to be omitted from deps to match the intended behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  if (selectedSystem && selectedSystem.location && (!selectedSystem.location.longitude || !selectedSystem.location.latitude)) {
    onError({error: `Unknown coordinates for system ${systemId}.`, specific: true});
    return null;
  }
  if (loadError) {
    onError(loadError);
    return null;
  }
  if (error) {
    onError(error);
    return null;
  }
  if (!data || !data.systems) {
    return null;
  }

  const updateSelectedQS = system => {
    history.push(`/map?system=${system.id}`);
  };

  const updateZoomPropOnZoom = () => {
    if (map.current) {
      setZoom(map.current && map.current && map.current.state.map.zoom);
    }
  };

  if (!isLoaded) {
    return <Loading loading message="Loading map..." />;
  }
  return (
    <Wrapper>
      <GoogleMap
        ref={map}
        center={center}
        zoom={zoom}
        onZoomChanged={updateZoomPropOnZoom}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          minZoom: 3,
        }}
      >
        <MapContent
          systems={filterSystems(data.systems || [])}
          selectedSystem={selectedSystem}
          onMarkerClick={updateSelectedQS}
          onInfoWindowClose={() => {
            history.push('/map');
          }}
        />
        );
      </GoogleMap>
    </Wrapper>
  );
};

GlobalMapPage.propTypes = {
  onError: PropTypes.func.isRequired,
  history: PropTypes.object,
};

export default withRouter(GlobalMapPage);
